import {useSearchParams} from 'react-router-dom'
import {DEFAULT_PAGE} from '../../../../_gori/constants'
import {Dispatch, SetStateAction, useState} from 'react'
import {CreateClaimsModal} from './CreateClaimsModal'
import {Modal} from 'react-bootstrap'
import {Button, InputTextFormik} from '../../../../_gori/partials/widgets'
import {useIntl} from 'react-intl'
import ClaimService from '../core/_requests'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {get, omit} from 'lodash'
import {useFormik} from 'formik'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import * as Yup from 'yup'

type TrackingNumberClaimModalProps = {
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  onCreateClaimsSuccessCallback?: () => void
  dataInit?: {
    tracking_number: string
    claim_recipient_phone: string
    claim_recipient_email?: string
  }
}
export const TrackingNumberClaimModal = ({
  show,
  setShow,
  onCreateClaimsSuccessCallback,
  dataInit,
}: TrackingNumberClaimModalProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const {newCancelToken, isCancel} = useCancelToken()

  const [showModalNewClaim, setShowModalNewClaim] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const {stringYup} = UseYupValidate()
  const [dataShipment, setDataShipment] = useState<{
    carrier: string
    insurance_type: string | null
  }>({
    carrier: '',
    insurance_type: null,
  })

  const intl = useIntl()

  const validateSchema = Yup.object({
    tracking_number: stringYup(50, 'TRACKING_NUMBER'),
  })

  const formik = useFormik({
    initialValues: {
      tracking_number: dataInit?.tracking_number || '',
    },
    enableReinitialize: true,
    validationSchema: validateSchema,
    onSubmit: async (values) => {
      await onCheckTrackingNumber(values.tracking_number)
    },
  })

  const handleCreateClaimsSuccess = () => {
    if (!(searchParams.get('search_text') === '' && searchParams.get('page') === DEFAULT_PAGE)) {
      searchParams.delete('search_text')
      searchParams.set('page', DEFAULT_PAGE)
      setSearchParams(searchParams)
    }
    onCreateClaimsSuccessCallback?.()
  }

  const handleCloseModal = () => {
    formik.resetForm()
    setShow(false)
  }

  const onCheckTrackingNumber = async (trackingNumber: string) => {
    try {
      setLoading(true)
      const res = await ClaimService.checkTrackingNumber(trackingNumber, {
        cancelToken: newCancelToken(),
      })

      setDataShipment({
        carrier: res?.carrier || '',
        insurance_type: res?.insurance_type || null,
      })
      setShowModalNewClaim(true)
    } catch (error: any) {
      if (isCancel(error)) return
      formik.setFieldError(
        'tracking_number',
        get(
          error,
          'response.data.error.message',
          `${intl.formatMessage({id: 'SOMETHING_WENT_WRONG'})} ${intl.formatMessage({
            id: 'PLEASE_TRY_AGAIN_LATER',
          })}`
        )
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <Modal
        id='gori_modal_tracking_number_claim'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-lg h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {intl.formatMessage({id: 'CLAIM_FORM'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='vh-75 overflow-auto'>
          <div className='mb-13 text-center'>
            <h1 className='mb-3'>{intl.formatMessage({id: 'START_YOUR_CLAIM'})}</h1>
            <div className='text-muted fw-semibold fs-5'>
              {intl.formatMessage({
                id: 'ENTER_TRACKING_NUMBER',
              })}
            </div>
          </div>
          <div className='d-flex gap-4'>
            <span className='text-gray-800 mt-4 fw-bold'>
              {intl.formatMessage({id: 'TRACKING_NUMBER'})}
              <span className='text-danger'>*</span>
            </span>
            <div className='d-flex flex-fill flex-column'>
              <InputTextFormik name={'tracking_number'} formik={formik} disabled={loading} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({id: 'NEXT'})}
              loadingText={intl.formatMessage({id: 'NEXT'})}
              event={formik.submitForm}
              loading={loading}
            />
          </div>
        </Modal.Footer>
      </Modal>

      <CreateClaimsModal
        trackingNumber={formik.values.tracking_number}
        show={showModalNewClaim}
        handleClose={() => {
          setShowModalNewClaim(false)
        }}
        handleCreateClaimsSuccess={handleCreateClaimsSuccess}
        onCancelCallback={() => {
          setShowModalNewClaim(false)
          handleCloseModal()
        }}
        dataShipment={dataShipment}
        dataInit={dataInit ? omit(dataInit, ['tracking_number']) : undefined}
      />
    </div>
  )
}
